import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Typography } from 'antd';
import { createEventSource } from 'eventsource-client';
import moment from 'moment';
import { MdError, MdVideoSettings } from 'react-icons/md';
import TMIcon from 'tm-icons-library';

import config from '../../../api/config';
import { useTheme } from '../../../context/ThemeProvider';
import { useVideoTranscodingJobDetails } from '../../../hooks/queries/fileQueries';
import { secondsToHmsTime, useAppSelector } from '../../../shared/hooks';
import {
  ITranscodingJobEventDetails,
  TRANSCODING_JOB_EVENT_VIDEO_PROCESSING_STATUS_MAP,
  TranscodingJobEvent,
  TranscodingJobEventVideoProcessingStatusType,
  TranscodingJobStatus,
  VIDEO_PROCESSING_STATUS_ORDER,
  VIDEO_PROCESSING_STATUS_TITLE,
  VideoProcessingStatusType,
} from '../../../types/fileTypes';
import Loader from '../../V2/Loader/Loader';

const ICON_SIZE = 24;

const TOTAL_STEPS = Object.keys(VIDEO_PROCESSING_STATUS_ORDER).length;

const ERROR_TITLE = "This video can't be played";
const ERROR_SUBTITLE = 'Try uploading again or contact support.';
const ERROR_SUBTITLE_FOR_OTHERS = 'Something went wrong with this video.';

interface Props {
  transcodingJobId?: string;
  hasError?: boolean;
  errorDescription?: string;
  errorSubtitle?: string;
  title?: string;
  subtitle?: string;
  showProcessingStatus?: boolean;
  onProcessingComplete?: () => void;
}

const VideoProcessing: React.FC<Props> = ({
  transcodingJobId,
  hasError = false,
  errorDescription = ERROR_TITLE,
  errorSubtitle,
  title = 'Preparing video',
  subtitle = 'The video will be ready shortly.',
  showProcessingStatus = false,
  onProcessingComplete,
}) => {
  const { colors: COLORS } = useTheme();

  const { token } = useAppSelector((state) => state.app);

  const {
    data: transcodingJobDetails,
    isLoading: isLoadingTranscodingJobDetails,
    error: transcodingJobDetailsError,
  } = useVideoTranscodingJobDetails(
    transcodingJobId as string,
    Boolean(transcodingJobId) && showProcessingStatus,
  );

  const [transcodingEvent, setTranscodingEvent] =
    useState<ITranscodingJobEventDetails>();

  const {
    shouldShowProcessingStatus,
    videoTranscodingInQueue,
    videoNotProcessedYetAfter8Hours,
    videoProcessingCompleted,
    videoProcessingFailed,
  } = useMemo(() => {
    return {
      // check if the transcoding job ID is present and showProcessingStatus is true, then show the processing status to the video owner only
      shouldShowProcessingStatus:
        Boolean(transcodingJobId) && showProcessingStatus,
      // check if the transcoding job is in queue and the job has not started yet and the transcoding event is not started yet, then the video transcoding is still in queue
      videoTranscodingInQueue:
        transcodingJobDetails?.status === TranscodingJobStatus.QUEUED &&
        !transcodingJobDetails?.jobStartedAt &&
        !(
          transcodingEvent?.event &&
          Object.keys(
            TRANSCODING_JOB_EVENT_VIDEO_PROCESSING_STATUS_MAP,
          ).includes(
            transcodingEvent?.event as TranscodingJobEventVideoProcessingStatusType,
          )
        ),
      // check if the video processing is not completed yet after 8 hours (since either the job started at or the job created at)
      videoNotProcessedYetAfter8Hours:
        (transcodingJobDetails?.jobStartedAt &&
          moment().diff(moment(transcodingJobDetails.jobStartedAt), 'hours') >
            8) ||
        (transcodingJobDetails?.createdAt &&
          moment().diff(moment(transcodingJobDetails.createdAt), 'hours') > 8),
      // check if the video processing is completed
      videoProcessingCompleted:
        transcodingJobDetails?.status === TranscodingJobStatus.COMPLETED ||
        transcodingEvent?.event === TranscodingJobEvent.COMPLETED,
      // check if the video processing is failed
      videoProcessingFailed:
        transcodingJobDetails?.status === TranscodingJobStatus.FAILED ||
        transcodingEvent?.event === TranscodingJobEvent.FAILED,
    };
  }, [
    transcodingJobId,
    showProcessingStatus,
    transcodingJobDetails?.status,
    transcodingJobDetails?.jobStartedAt,
    transcodingJobDetails?.createdAt,
    transcodingEvent?.event,
  ]);

  useEffect(() => {
    // don't start the SSE connection if:
    // - there is no transcoding job ID
    // - showProcessingStatus is false
    // - the transcoding job is completed
    // - the transcoding job is failed
    if (
      !transcodingJobId ||
      !showProcessingStatus ||
      transcodingJobDetails?.status === TranscodingJobStatus.COMPLETED ||
      transcodingJobDetails?.status === TranscodingJobStatus.FAILED
    ) {
      return;
    }

    // start SSE connection for video transcoding job details to show the processing status in real-time
    const eventSource = createEventSource({
      url: `${config.API_ENDPOINT}/api/v1/video-encoding/progress/${transcodingJobId}`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
      onMessage: (event) => {
        // don't process the message if the event is 'error',
        // don't close the SSE connection because it will be closed by the client when the transcoding job is completed or failed (edge case: it can be in queue for a long time)
        if (event.event === 'error') {
          return;
        }

        const data: ITranscodingJobEventDetails = JSON.parse(event.data);

        setTranscodingEvent((prev) => {
          if (!prev) return data;

          return {
            ...prev,
            ...data,
          };
        });

        // close the SSE connection when the transcoding job is completed or failed
        if (
          data.event === TranscodingJobEvent.COMPLETED ||
          data.event === TranscodingJobEvent.FAILED
        ) {
          eventSource.close();
        }
      },
    });

    return () => {
      eventSource.close();
    };
  }, [
    transcodingJobId,
    token,
    showProcessingStatus,
    transcodingJobDetails?.status,
  ]);

  const renderProcessingStep = useCallback(
    (
      step: VideoProcessingStatusType,
      options?: {
        completed?: boolean;
        running?: boolean;
        remainingTime?: number;
      },
    ) => {
      const { completed, running, remainingTime } = options || {};

      let icon = (
        <TMIcon
          className="step__icon"
          name="ellipse-outline"
          size={ICON_SIZE}
          color={COLORS.BACKGROUND}
        />
      );

      if (completed) {
        icon = (
          <TMIcon
            className="step__icon"
            name="checkmark-circle"
            size={ICON_SIZE}
            color={COLORS.GREEN}
          />
        );
      } else if (running) {
        icon = (
          <Loader
            className="step__icon"
            color={COLORS.BACKGROUND}
            loadingStyle={{
              fontSize: 20,
            }}
            style={{
              display: 'inline-block',
              margin: 0,
            }}
          />
        );
      }

      return (
        <div className="videoProgress__processing-step">
          {icon}
          <div className="step__content">
            <Typography.Text className="step__content__title">
              Step {`${VIDEO_PROCESSING_STATUS_ORDER[step]}/${TOTAL_STEPS}`} -{' '}
              {VIDEO_PROCESSING_STATUS_TITLE[step]}
            </Typography.Text>
            <Typography.Text className="step__content__info">
              {(completed && 'Done') ||
                (running &&
                  typeof remainingTime === 'number' &&
                  `${secondsToHmsTime(remainingTime).displayTimeText} left`) ||
                ''}
            </Typography.Text>
          </div>
        </div>
      );
    },
    [COLORS.BACKGROUND, COLORS.GREEN],
  );

  const content = useMemo(() => {
    const state: {
      icon: React.ReactNode;
      title: string;
      subtitle: React.ReactNode;
    } = {
      icon: (
        <MdVideoSettings
          className="videoProgress__icon"
          size={50}
          color={COLORS.BACKGROUND}
          style={{ marginBottom: 12 }}
        />
      ),
      title,
      subtitle,
    };

    // check if the video has an error
    if (hasError) {
      state.icon = (
        <MdError
          className="videoProgress__icon"
          size={50}
          color={COLORS.DANGER}
          style={{
            marginBottom: 12,
          }}
        />
      );
      state.title = errorDescription;
      state.subtitle =
        errorSubtitle ||
        (showProcessingStatus ? ERROR_SUBTITLE : ERROR_SUBTITLE_FOR_OTHERS);
      return state;
    }

    // check if the video processing status should be shown to the video owner only
    if (shouldShowProcessingStatus) {
      state.title = 'We’re getting your video ready!';

      // check if the video processing status is loading
      if (isLoadingTranscodingJobDetails) {
        state.subtitle = 'Loading processing status...';
        return state;
      }

      // check if the video processing status is not loading and there is an error
      if (!isLoadingTranscodingJobDetails && transcodingJobDetailsError) {
        state.title = 'Unable to load video processing status';
        state.subtitle = 'Please contact support if the issue persists.';
        return state;
      }

      // check if the video processing is not completed yet after 8 hours
      if (videoNotProcessedYetAfter8Hours) {
        state.subtitle =
          'This is taking longer than usual. We’re looking into it — please contact support if the issue persists.';
        return state;
      }

      // check if the video processing is failed
      if (videoProcessingFailed) {
        state.icon = (
          <MdError
            className="videoProgress__icon"
            size={50}
            color={COLORS.DANGER}
            style={{
              marginBottom: 12,
            }}
          />
        );
        state.title = ERROR_TITLE;
        state.subtitle = ERROR_SUBTITLE;
        return state;
      }
    }

    // fallback: check if the video processing is failed, then show the error messages accordingly to the other users except the video owner
    if (videoProcessingFailed) {
      state.icon = (
        <MdError
          className="videoProgress__icon"
          size={50}
          color={COLORS.DANGER}
          style={{
            marginBottom: 12,
          }}
        />
      );
      state.title = ERROR_TITLE;
      state.subtitle = ERROR_SUBTITLE_FOR_OTHERS;
      return state;
    }

    // check if the video processing is completed
    if (videoProcessingCompleted) {
      state.title = 'The video is ready!';
      state.subtitle = (
        <div className="videoProgress__processing-step">
          <TMIcon
            className="step__icon"
            name="checkmark-circle"
            size={ICON_SIZE}
            color={COLORS.GREEN}
          />
          <div className="step__content">Refresh the page to play.</div>
        </div>
      );
      onProcessingComplete?.(); // callback to notify the parent component that the video processing is complete and the video is ready to play
      return state;
    }

    return state;
  }, [
    COLORS.BACKGROUND,
    COLORS.DANGER,
    COLORS.GREEN,
    title,
    subtitle,
    hasError,
    showProcessingStatus,
    shouldShowProcessingStatus,
    videoProcessingCompleted,
    errorDescription,
    errorSubtitle,
    isLoadingTranscodingJobDetails,
    transcodingJobDetailsError,
    videoNotProcessedYetAfter8Hours,
    onProcessingComplete,
    videoProcessingFailed,
  ]);

  return (
    <div className="videoProgress">
      {content.icon}
      <Typography.Text className="videoProgressTitle">
        {content.title}
      </Typography.Text>
      {/* check if the video has no error and the video processing status should be shown to the video owner only and the video processing is not completed yet and the video processing is not failed and it's not stuck for more than 8 hours, then show the processing status and transcoding job details are successfully fetched */}
      {!hasError &&
      !isLoadingTranscodingJobDetails &&
      shouldShowProcessingStatus &&
      !videoProcessingCompleted &&
      !videoProcessingFailed &&
      !videoNotProcessedYetAfter8Hours ? (
        <>
          {videoTranscodingInQueue
            ? renderProcessingStep('in-queue', {
                running: true,
              })
            : (transcodingEvent?.event &&
                Object.keys(
                  TRANSCODING_JOB_EVENT_VIDEO_PROCESSING_STATUS_MAP,
                ).includes(
                  transcodingEvent?.event as TranscodingJobEventVideoProcessingStatusType,
                ) &&
                renderProcessingStep(
                  TRANSCODING_JOB_EVENT_VIDEO_PROCESSING_STATUS_MAP[
                    transcodingEvent.event as TranscodingJobEventVideoProcessingStatusType
                  ],
                  {
                    running:
                      !transcodingEvent?.progress ||
                      transcodingEvent.progress < 100,
                    remainingTime:
                      transcodingEvent?.estimatedTimeLeftBeforeCompletion,
                  },
                )) || (
                <Typography.Text className="videoProgressText">
                  This may take a moment...
                </Typography.Text>
              )}
        </>
      ) : (
        <Typography.Text className="videoProgressText">
          {content.subtitle}
        </Typography.Text>
      )}
    </div>
  );
};

export default React.memo(VideoProcessing);
